import shouldForwardProp from '@styled-system/should-forward-prop'

import styled from 'styled-components'
import { space, layout, color, flexbox, grid, position, typography, borderRadius, display, overflow, maxWidth } from 'styled-system'
import { Box, Flex, gap } from './Primitives'

/**
 * Fixed component.
 *
 * A basic layout component that applies fixed positioning to its children.
 *
 * @component
 * @example
 * // Usage example:
 * <Fixed p={3} left={25} top={50}>
 *   Fixed Content
 * </Fixed>
 *
 * @param {Object} props - The props that are passed to the component.
 * @param {Object} [props.css] - Custom CSS styles to apply to the component.
 * @returns {JSX.Element} The Fixed component.
 */
export const Fixed = styled(Box)({
	position: 'fixed',
})

/**
 * Absolute component.
 *
 * A basic layout component that applies absolute positioning to its children.
 *
 * @component
 * @example
 * // Usage example:
 * <Absolute p={3} left={25} top={50}>
 *   Absolute Content
 * </Absolute>
 *
 * @param {Object} props - The props that are passed to the component.
 * @param {Object} [props.css] - Custom CSS styles to apply to the component.
 * @returns {JSX.Element} The Absolute component.
 */
export const Absolute = styled(Box)({
	position: 'absolute',
})

/**
 * Hide component.
 *
 * This component conditionally hides its children based on the provided breakpoints.
 *
 * @component
 * @example
 * // Usage example:
 * <Hide xs display="none">
 *   Hidden Content
 * </Hide>
 *
 * @param {Object} props - The props that are passed to the component.
 * @param {boolean} [props.xs] - Hide on extra small screens.
 * @param {boolean} [props.s] - Hide on small screens.
 * @param {boolean} [props.m] - Hide on medium screens.
 * @param {boolean} [props.l] - Hide on large screens.
 * @param {string} [props.display] - The display property to apply when hidden.
 * @returns {JSX.Element} The Hide component.
 */

export const Hide = ({ xs, s, m, l, children, ...props }) => {
  return (
    <Flex
      display={[
        xs ? 'none' : 'flex',
        s ? 'none' : 'flex',
        m ? 'none' : 'flex',
        l ? 'none' : 'flex'
      ]}
      {...props}
    >
      {children}
    </Flex>
  )
}

/**
 * LayoutGrid component.
 * 
 * This creates a grid with columns that change at different breakpoints.
 * 
 * @component
 * @example
 * <LayoutGrid>
 *   <Flex gridColumn="1 / -1">Content</Flex>
 *   <Flex gridColumn="2 / -2">Content</Flex>
 * <LayoutGrid>
 * 
 * @param {Object} props - The props that are passed to the component.
 * @param {Object} [props.css] - Custom CSS styles to apply to the component.
 * @returns {JSX.Element} The Absolute component.
 */
export const LayoutGrid = styled('section')
  .withConfig({
    shouldForwardProp
  })
  .attrs(props => ({
    display: 'grid',
    position: 'relative',
    gridColumnGap: 4,
    alignItems: 'center',
    justifyContent: 'center',
    gridTemplateColumns: [
      'repeat(6, 1fr)',
      'repeat(6, 1fr)',
      'repeat(6, 1fr)',
      'repeat(12, 1fr)'
    ],
    gridAutoRows: 'auto',
    ...props
  }))(
  {},
  space,
  layout,
  color,
  flexbox,
  grid,
  position,
  gap,
  typography,
  borderRadius,
  display,
  overflow
)

export const Container = styled(Box)
	.withConfig({ shouldForwardProp })
	.attrs((props) => ({
		mx: 'auto',
		px: 3,
    maxWidth: 1360,
		...props,
	}))({}, maxWidth)
